import React, {useEffect, useRef, useState} from 'react'
import Hls from 'hls.js'
import {Box, CircularProgress, Stack, Typography} from '@mui/material'
import {useShowError} from '@hooks/useShowError/useShowError'
import {getRecordingUrlApi} from '@containers/monitoringPlatform/geoZones/api/api'

interface IProps {
	accountId: number
	recordingId: number
}

const buildPlaylistUrl = (url: string, durationSeconds: number): string => {
	const duration = Math.max(Math.ceil(durationSeconds || 0), 1)
	const playlist = [
		'#EXTM3U',
		'#EXT-X-VERSION:3',
		`#EXT-X-TARGETDURATION:${duration}`,
		'#EXT-X-MEDIA-SEQUENCE:0',
		'#EXT-X-PLAYLIST-TYPE:VOD',
		`#EXTINF:${duration}.0,`,
		url,
		'#EXT-X-ENDLIST',
	].join('\n')
	return URL.createObjectURL(new Blob([playlist], {type: 'application/vnd.apple.mpegurl'}))
}

export const RecordingPlayer = ({accountId, recordingId}: IProps) => {
	const {onShowError} = useShowError()
	const videoRef = useRef<HTMLVideoElement>(null)
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [error, setError] = useState<string>('')

	useEffect(() => {
		let hls: Hls | null = null
		let playlistUrl: string | null = null
		let cancelled = false

		setIsLoading(true)
		setError('')

		getRecordingUrlApi({accountId, recordingId}, onShowError)
			.then((data) => {
				const video = videoRef.current
				if (cancelled || !video) {
					return
				}
				if (!data?.url) {
					setError('Не вдалося отримати посилання на відео')
					return
				}
				playlistUrl = buildPlaylistUrl(data.url, data.durationSeconds)

				if (Hls.isSupported()) {
					hls = new Hls()
					hls.on(Hls.Events.ERROR, (_, errorData) => {
						if (errorData.fatal) {
							setError('Не вдалося відтворити відео')
						}
					})
					hls.loadSource(playlistUrl)
					hls.attachMedia(video)
					return
				}
				if (video.canPlayType('application/vnd.apple.mpegurl')) {
					video.src = playlistUrl
					return
				}
				setError('Браузер не підтримує відтворення цього відео')
			})
			.finally(() => {
				if (!cancelled) {
					setIsLoading(false)
				}
			})

		return () => {
			cancelled = true
			hls?.destroy()
			if (playlistUrl) {
				URL.revokeObjectURL(playlistUrl)
			}
		}
	}, [accountId, recordingId])

	return (
		<Box sx={{position: 'relative', width: '100%', bgcolor: 'black', borderRadius: 1, overflow: 'hidden'}}>
			<Box
				component={'video'}
				ref={videoRef}
				controls
				autoPlay
				muted
				playsInline
				sx={{display: 'block', width: '100%', maxHeight: '75vh', aspectRatio: '16 / 9'}}
			/>
			{(isLoading || error) && (
				<Stack
					alignItems="center"
					justifyContent="center"
					sx={{position: 'absolute', inset: 0, pointerEvents: 'none'}}
				>
					{isLoading
						? <CircularProgress size={32}/>
						: <Typography variant="body2" color="error.light">{error}</Typography>
					}
				</Stack>
			)}
		</Box>
	)
}

export default RecordingPlayer
